import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trash2, Plus, CalendarX, FileText, ArrowLeft, LogOut, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminPin } from "@/components/AdminPinGate";
import { hexToHslString, darkenHslString } from "@/lib/siteContent";

function useAdminApi() {
  const { pin, clear } = useAdminPin();
  const wrapped = useMemo(() => {
    const headers = { "x-admin-pin": pin };
    return {
      get: (u, cfg = {}) => api.get(u, { ...cfg, headers: { ...(cfg.headers || {}), ...headers } }),
      post: (u, b, cfg = {}) => api.post(u, b, { ...cfg, headers: { ...(cfg.headers || {}), ...headers } }),
      put: (u, b, cfg = {}) => api.put(u, b, { ...cfg, headers: { ...(cfg.headers || {}), ...headers } }),
      delete: (u, cfg = {}) => api.delete(u, { ...cfg, headers: { ...(cfg.headers || {}), ...headers } }),
    };
  }, [pin]);
  return { adminApi: wrapped, logout: clear };
}

export default function Admin() {
  const [tab, setTab] = useState("bookings");
  const { logout } = useAdminApi();
  return (
    <div data-surface="audit" className="min-h-screen bg-[hsl(var(--audit-bg))] text-[hsl(var(--audit-text))]">
      <header className="border-b border-[hsl(var(--audit-border))] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--audit-muted))]">Admin</div>
            <div className="mt-1 text-xl font-bold">Panel interno</div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--audit-muted))]"><ArrowLeft size={14} /> Web pública</Link>
            <button onClick={() => { logout(); window.location.reload(); }} data-testid="admin-logout" className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-1.5 text-xs font-semibold text-red-700"><LogOut size={12} /> Cerrar</button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-6 pt-6">
        <div className="flex gap-2 border-b border-[hsl(var(--audit-border))]">
          {[["bookings","Reservas"],["blocked","Días bloqueados"],["blog","Blog"],["testimonials","Testimonios"],["patients","Pacientes"],["content","Contenido web"]].map(([k,l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`border-b-2 px-4 py-3 text-sm font-semibold ${
                tab === k ? "border-[hsl(var(--audit-text))]" : "border-transparent text-[hsl(var(--audit-muted))]"
              }`}
            >{l}</button>
          ))}
        </div>
        <div className="py-8">
          {tab === "bookings" && <BookingsAdmin />}
          {tab === "blocked" && <BlockedAdmin />}
          {tab === "blog" && <BlogAdmin />}
          {tab === "testimonials" && <TestimonialsAdmin />}
          {tab === "patients" && <PatientsAdmin />}
          {tab === "content" && <ContentAdmin />}
        </div>
      </div>
    </div>
  );
}

function BookingsAdmin() {
  const { adminApi } = useAdminApi();
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/bookings").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta reserva?")) return;
    await api.delete(`/bookings/${id}`); toast.success("Cancelada"); load();
  };
  return (
    <div>
      <div className="mb-4 text-sm text-[hsl(var(--audit-muted))]">{items.length} reservas totales</div>
      <div className="overflow-hidden rounded-md border border-[hsl(var(--audit-border))] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-[hsl(var(--audit-border))] bg-[hsl(var(--audit-bg))] font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">
            <tr><th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Hora</th><th className="px-4 py-3">Paciente</th><th className="px-4 py-3">Tratamiento</th><th className="px-4 py-3">Contacto</th><th className="px-4 py-3">Recordatorio</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[hsl(var(--audit-muted))]">Sin reservas.</td></tr>}
            {items.map((b) => (
              <tr key={b.id} className="border-b border-[hsl(var(--audit-border))] last:border-0" data-testid={`admin-booking-${b.id}`}>
                <td className="px-4 py-3 font-mono">{b.slot_date}</td>
                <td className="px-4 py-3 font-mono">{b.slot_time}</td>
                <td className="px-4 py-3">{b.patient_name}</td>
                <td className="px-4 py-3">{b.treatment_name}</td>
                <td className="px-4 py-3 text-xs text-[hsl(var(--audit-muted))]">{b.patient_email}<br/>{b.patient_phone}</td>
                <td className="px-4 py-3 text-xs">{b.reminder_sent ? <span className="text-emerald-700">Enviado</span> : <span className="text-[hsl(var(--audit-muted))]">Pendiente</span>}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => cancel(b.id)} data-testid={`admin-cancel-${b.id}`} className="text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs italic text-[hsl(var(--audit-muted))]">Recordatorios 24h antes: MOCKED (log). Se activarán con las keys de Resend/Twilio.</p>
    </div>
  );
}

function BlockedAdmin() {
  const { adminApi } = useAdminApi();
  const [items, setItems] = useState([]);
  const [day, setDay] = useState("");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get("/blocked-days").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/blocked-days", { day, reason });
      toast.success("Día bloqueado"); setDay(""); setReason(""); load();
    } catch { toast.error("Error al bloquear"); }
  };
  const remove = async (d) => {
    await adminApi.delete(`/blocked-days/${d}`); toast.success("Día liberado"); load();
  };
  return (
    <div>
      <form onSubmit={add} className="mb-6 flex flex-wrap items-end gap-3 rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
        <label className="flex flex-col">
          <span className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--audit-muted))]">Día</span>
          <input required type="date" data-testid="block-day-input" value={day} onChange={(e) => setDay(e.target.value)} className="mt-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
        </label>
        <label className="flex flex-1 min-w-[200px] flex-col">
          <span className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--audit-muted))]">Motivo</span>
          <input placeholder="Vacaciones, festivo…" data-testid="block-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
        </label>
        <button type="submit" data-testid="block-day-submit" className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white"><CalendarX size={14} /> Bloquear</button>
      </form>
      <div className="overflow-hidden rounded-md border border-[hsl(var(--audit-border))] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-[hsl(var(--audit-border))] bg-[hsl(var(--audit-bg))] font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">
            <tr><th className="px-4 py-3">Día</th><th className="px-4 py-3">Motivo</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-[hsl(var(--audit-muted))]">Sin días bloqueados.</td></tr>}
            {items.map((b) => (
              <tr key={b.day} className="border-b border-[hsl(var(--audit-border))] last:border-0">
                <td className="px-4 py-3 font-mono">{b.day}</td>
                <td className="px-4 py-3 text-[hsl(var(--audit-muted))]">{b.reason || "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(b.day)} data-testid={`unblock-${b.day}`} className="text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BlogAdmin() {
  const { adminApi } = useAdminApi();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { slug: "", title: "", excerpt: "", body_markdown: "", tags: "", published: true };
  const [form, setForm] = useState(empty);
  const load = useCallback(() => api.get("/blog?published_only=false").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await adminApi.put(`/blog/${editing}`, payload);
      else await adminApi.post("/blog", payload);
      toast.success(editing ? "Actualizado" : "Creado");
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const startEdit = async (slug) => {
    const r = await api.get(`/blog/${slug}`);
    const p = r.data;
    setForm({ slug: p.slug, title: p.title, excerpt: p.excerpt, body_markdown: p.body_markdown, tags: (p.tags || []).join(", "), published: p.published });
    setEditing(p.id); setShowForm(true);
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar entrada?")) return;
    await adminApi.delete(`/blog/${id}`); toast.success("Eliminada"); load();
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-[hsl(var(--audit-muted))]">{items.length} entradas</div>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(empty); }} data-testid="blog-new-btn" className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white">
          <Plus size={14} /> {showForm ? "Cerrar" : "Nueva entrada"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} data-testid="blog-form" className="mb-6 grid gap-3 rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
          <input required placeholder="slug (kebab-case)" data-testid="blog-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" disabled={!!editing} />
          <input required placeholder="Título" data-testid="blog-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
          <input required placeholder="Extracto (1–2 frases)" data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
          <textarea required rows={10} placeholder="Cuerpo en Markdown" data-testid="blog-body" value={form.body_markdown} onChange={(e) => setForm({ ...form, body_markdown: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 font-mono text-xs" />
          <input placeholder="Tags separados por coma" data-testid="blog-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publicada</label>
          <button type="submit" data-testid="blog-submit" className="w-fit rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white">{editing ? "Actualizar" : "Crear"}</button>
        </form>
      )}
      <div className="overflow-hidden rounded-md border border-[hsl(var(--audit-border))] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-[hsl(var(--audit-border))] bg-[hsl(var(--audit-bg))] font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">
            <tr><th className="px-4 py-3">Título</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.slug} className="border-b border-[hsl(var(--audit-border))] last:border-0" data-testid={`admin-blog-${p.slug}`}>
                <td className="px-4 py-3 font-semibold">{p.title}</td>
                <td className="px-4 py-3 font-mono text-xs text-[hsl(var(--audit-muted))]">{p.slug}</td>
                <td className="px-4 py-3">{p.published ? <span className="text-emerald-700">Publicada</span> : <span className="text-amber-700">Borrador</span>}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => startEdit(p.slug)} className="mr-3 text-sm hover:underline"><FileText size={14} className="inline" /> Editar</button>
                  <button onClick={() => remove(p.id)} data-testid={`admin-blog-delete-${p.slug}`} className="text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PatientsAdmin() {
  const { adminApi } = useAdminApi();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [nextMonths, setNextMonths] = useState("");
  const [treatmentFocus, setTreatmentFocus] = useState("");
  const load = useCallback(() => adminApi.get("/admin/patients").then((r) => setItems(r.data)), [adminApi]);
  useEffect(() => { load(); }, [load]);
  const openDetail = async (id) => {
    setSelected(id);
    const r = await adminApi.get(`/admin/patients/${id}`);
    setDetail(r.data);
  };
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const months = nextMonths ? parseInt(nextMonths, 10) : null;
      await adminApi.post(`/admin/patients/${selected}/notes`, {
        note: newNote,
        next_visit_months: Number.isFinite(months) && months > 0 ? months : null,
        treatment_focus: treatmentFocus || null,
      });
      toast.success("Nota añadida"); setNewNote(""); setNextMonths(""); setTreatmentFocus(""); openDetail(selected);
    } catch { toast.error("Error"); }
  };
  const delNote = async (nid) => {
    if (!window.confirm("¿Eliminar nota?")) return;
    await adminApi.delete(`/admin/notes/${nid}`); openDetail(selected);
  };
  const downloadPdf = async () => {
    const resp = await adminApi.get(`/admin/patients/${selected}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = `historial-${detail.user.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
      <div className="rounded-md border border-[hsl(var(--audit-border))] bg-white p-2">
        <div className="border-b border-[hsl(var(--audit-border))] px-3 py-2 font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">{items.length} pacientes</div>
        <div className="max-h-[600px] overflow-auto">
          {items.map((p) => (
            <button key={p.id} onClick={() => openDetail(p.id)} data-testid={`admin-patient-${p.id}`} className={`block w-full border-b border-[hsl(var(--audit-border))] px-3 py-3 text-left last:border-0 hover:bg-[hsl(var(--audit-bg))] ${selected === p.id ? "bg-[hsl(var(--audit-bg))]" : ""}`}>
              <div className="text-sm font-semibold">{p.name}</div>
              <div className="text-xs text-[hsl(var(--audit-muted))]">{p.email}</div>
              <div className="text-xs text-[hsl(var(--audit-muted))]">{p.phone || "—"}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        {!detail && <div className="rounded-md border border-dashed border-[hsl(var(--audit-border))] p-10 text-center text-sm text-[hsl(var(--audit-muted))]">Selecciona un paciente para ver su historial y añadir notas clínicas.</div>}
        {detail && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
              <div>
                <div className="text-xl font-bold">{detail.user.name}</div>
                <div className="text-xs text-[hsl(var(--audit-muted))]">{detail.user.email} · {detail.user.phone || "—"} · {detail.user.dob || "sin fecha"}</div>
              </div>
              <button onClick={downloadPdf} data-testid="admin-patient-pdf" className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-1.5 text-xs font-semibold">Descargar PDF</button>
            </div>
            <div className="rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
              <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Historial autoreportado</div>
              {(() => { const h = detail.user.medical_history || {}; return (
                <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  {[["Alergias","allergies"],["Condiciones crónicas","chronic_conditions"],["Medicación actual","current_medications"],["Tratamientos previos","previous_treatments"],["Notas","notes"]].map(([l,k]) => (
                    <div key={k}><dt className="text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">{l}</dt><dd>{h[k] || "—"}</dd></div>
                  ))}
                </dl>
              ); })()}
            </div>
            <div className="rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Notas clínicas privadas</div>
                <span className="text-xs italic text-[hsl(var(--audit-muted))]">Visibles solo desde admin</span>
              </div>
              <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_180px_140px_auto]">
                <input value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="new-note-input" placeholder="Nueva nota clínica…" className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
                <input value={treatmentFocus} onChange={(e) => setTreatmentFocus(e.target.value)} data-testid="new-note-focus" placeholder="Tratamiento (opcional)" className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
                <input value={nextMonths} onChange={(e) => setNextMonths(e.target.value.replace(/[^0-9]/g,""))} data-testid="new-note-months" placeholder="Próximo control (meses)" title="Próximo control sugerido en meses" className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
                <button onClick={addNote} data-testid="add-note-btn" className="rounded-md bg-[hsl(var(--audit-text))] px-3 py-2 text-xs font-semibold text-white">Añadir</button>
              </div>
              <div className="space-y-2">
                {detail.notes.length === 0 && <p className="text-xs text-[hsl(var(--audit-muted))]">Aún no hay notas.</p>}
                {detail.notes.map((n) => (
                  <div key={n.id} data-testid={`clinical-note-${n.id}`} className="rounded-md border border-[hsl(var(--audit-border))] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-mono text-[10px] text-[hsl(var(--audit-muted))]">{new Date(n.created_at).toLocaleString("es-CO")}</span>
                      <div className="flex items-center gap-2">
                        {n.treatment_focus && <span className="rounded-full bg-[hsl(var(--audit-bg))] px-2 py-0.5 text-[10px] uppercase tracking-widest text-[hsl(var(--audit-muted))]">{n.treatment_focus}</span>}
                        {n.next_visit_months && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">próx. control: {n.next_visit_months} m{n.next_visit_suggested_at ? ` · ${n.next_visit_suggested_at}` : ""}</span>}
                        <button onClick={() => delNote(n.id)} data-testid={`delete-note-${n.id}`} className="text-xs text-red-600">Eliminar</button>
                      </div>
                    </div>
                    <p className="mt-1 text-sm">{n.note}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
              <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Citas ({detail.bookings.length})</div>
              <div className="space-y-1 text-sm">
                {detail.bookings.map((b) => (
                  <div key={b.id} className="flex justify-between border-b border-[hsl(var(--audit-border))] py-1 last:border-0">
                    <span className="font-mono">{b.slot_date} {b.slot_time}</span>
                    <span>{b.treatment_name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TestimonialsAdmin() {
  const { adminApi } = useAdminApi();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const empty = { patient_name: "", quote: "", rating: 5, treatment_slug: "", consent_signed: false, published: false };
  const [form, setForm] = useState(empty);
  const load = useCallback(() => api.get("/testimonials?published_only=false").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/testimonials", form);
      toast.success("Testimonio creado"); setShowForm(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar testimonio?")) return;
    await adminApi.delete(`/testimonials/${id}`); toast.success("Eliminado"); load();
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-[hsl(var(--audit-muted))]">{items.length} testimonios</div>
        <button onClick={() => setShowForm(!showForm)} data-testid="testimonial-new-btn" className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white">
          <Plus size={14} /> {showForm ? "Cerrar" : "Nuevo testimonio"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} data-testid="testimonial-form" className="mb-6 grid gap-3 rounded-md border border-[hsl(var(--audit-border))] bg-white p-4">
          <input required placeholder="Nombre del paciente" data-testid="t-patient-name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
          <textarea required rows={4} placeholder="Cita textual" data-testid="t-quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
          <div className="flex gap-3">
            <label className="flex flex-col text-xs">Estrellas
              <input type="number" min={1} max={5} data-testid="t-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value, 10) || 5 })} className="mt-1 w-20 rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
            </label>
            <label className="flex flex-1 flex-col text-xs">Tratamiento (slug opcional)
              <input placeholder="p.ej. ortodoncia" data-testid="t-treatment" value={form.treatment_slug} onChange={(e) => setForm({ ...form, treatment_slug: e.target.value })} className="mt-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input required type="checkbox" data-testid="t-consent" checked={form.consent_signed} onChange={(e) => setForm({ ...form, consent_signed: e.target.checked })} />
            Confirmo que el paciente firmó consentimiento escrito para publicar este testimonio.
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="t-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Publicar en la web pública
          </label>
          <button type="submit" data-testid="t-submit" className="w-fit rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white">Guardar</button>
        </form>
      )}
      <div className="overflow-hidden rounded-md border border-[hsl(var(--audit-border))] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-[hsl(var(--audit-border))] bg-[hsl(var(--audit-bg))] font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">
            <tr><th className="px-4 py-3">Paciente</th><th className="px-4 py-3">Cita</th><th className="px-4 py-3">Rating</th><th className="px-4 py-3">Consentimiento</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[hsl(var(--audit-muted))]">Sin testimonios.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-b border-[hsl(var(--audit-border))] last:border-0" data-testid={`admin-t-${t.id}`}>
                <td className="px-4 py-3 font-semibold">{t.patient_name}</td>
                <td className="px-4 py-3 text-[hsl(var(--audit-muted))]">"{t.quote.slice(0, 80)}{t.quote.length > 80 ? "…" : ""}"</td>
                <td className="px-4 py-3 flex items-center gap-0.5">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={`${t.id}-star-${i}`} size={12} className="fill-amber-500 text-amber-500" />)}</td>
                <td className="px-4 py-3">{t.consent_signed ? <span className="text-emerald-700">Firmado</span> : <span className="text-red-700">Falta</span>}</td>
                <td className="px-4 py-3">{t.published ? <span className="text-emerald-700">Publicado</span> : <span className="text-amber-700">Borrador</span>}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(t.id)} data-testid={`admin-t-delete-${t.id}`} className="text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContentAdmin() {
  const { adminApi } = useAdminApi();
  const [content, setContent] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);
  const load = useCallback(() => api.get("/site-content").then((r) => setContent(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setField = (section, field, value) => {
    setContent((c) => ({ ...c, [section]: { ...c[section], [field]: value } }));
    setDirty((d) => ({ ...d, [section]: true }));
  };
  const save = async (section) => {
    setSaving(section);
    try {
      await adminApi.put(`/site-content/${section}`, content[section]);
      toast.success(`Sección "${section}" actualizada`);
      setDirty((d) => ({ ...d, [section]: false }));
      // If theme colors saved, reload page to re-apply CSS variables globally
      if (section === "theme_colors") setTimeout(() => window.location.reload(), 600);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(null); }
  };
  const reset = async (section) => {
    if (!window.confirm("¿Restaurar valores por defecto de esta sección?")) return;
    try {
      const r = await adminApi.post(`/site-content/${section}/reset`);
      setContent((c) => ({ ...c, [section]: r.data }));
      setDirty((d) => ({ ...d, [section]: false }));
      toast.success("Restaurado");
      if (section === "theme_colors") setTimeout(() => window.location.reload(), 600);
    } catch { toast.error("Error"); }
  };

  const SECTIONS = [
    { key: "home_hero", label: "Home · Hero principal",
      fields: [["badge","Badge (etiqueta arriba)"],["title1","Título línea 1"],["title2","Título línea 2 (resaltado)"],["title3","Título línea 3"],
               ["subtitle","Subtítulo/Descripción",true],["cta_primary_label","Texto botón principal"],["cta_primary_link","Link botón principal"],
               ["cta_secondary_label","Texto botón secundario"],["cta_secondary_link","Link botón secundario"],["image_url","URL imagen hero"]] },
    { key: "home_trust", label: "Home · Tarjetas de confianza",
      fields: [["card1_title","Tarjeta 1 título"],["card1_note","Tarjeta 1 nota"],["card2_title","Tarjeta 2 título"],["card2_note","Tarjeta 2 nota"],
               ["card3_title","Tarjeta 3 título"],["card3_note","Tarjeta 3 nota"]] },
    { key: "home_treatments_intro", label: "Home · Intro tratamientos",
      fields: [["eyebrow","Eyebrow"],["title","Título"]] },
    { key: "home_confidence", label: "Home · Sección Confianza",
      fields: [["eyebrow","Eyebrow"],["title","Título"],["body","Cuerpo",true],
               ["metric1_k","Métrica 1 clave"],["metric1_v","Métrica 1 valor"],
               ["metric2_k","Métrica 2 clave"],["metric2_v","Métrica 2 valor"],
               ["metric3_k","Métrica 3 clave"],["metric3_v","Métrica 3 valor"],
               ["metric4_k","Métrica 4 clave"],["metric4_v","Métrica 4 valor"]] },
    { key: "home_final_cta", label: "Home · CTA final",
      fields: [["title","Título"],["subtitle","Subtítulo",true],
               ["cta_primary_label","Botón 1 texto"],["cta_primary_link","Botón 1 link"],
               ["cta_secondary_label","Botón 2 texto"],["cta_secondary_link","Botón 2 link"]] },
    { key: "treatments_hero", label: "Tratamientos · Encabezado",
      fields: [["eyebrow","Eyebrow"],["title","Título"],["body","Descripción",true]] },
    { key: "about_hero", label: "Sobre nosotros · Encabezado + bloques",
      fields: [["eyebrow","Eyebrow"],["title_start","Título (inicio)"],["title_highlight","Título (resaltado)"],["title_end","Título (final)"],
               ["body","Descripción",true],["image_url","URL imagen equipo"],["image_caption","Pie de imagen"],
               ["block1_title","Bloque 1 título"],["block1_body","Bloque 1 cuerpo",true],
               ["block2_title","Bloque 2 título"],["block2_body","Bloque 2 cuerpo",true],
               ["block3_title","Bloque 3 título"],["block3_body","Bloque 3 cuerpo",true]] },
    { key: "contact_hero", label: "Contacto · Encabezado",
      fields: [["eyebrow","Eyebrow"],["title","Título"],["body","Descripción",true],["whatsapp_label","Texto botón WhatsApp"]] },
    { key: "faq_hero", label: "FAQ · Encabezado",
      fields: [["eyebrow","Eyebrow"],["title","Título"],["body","Descripción",true]] },
    { key: "footer", label: "Footer",
      fields: [["tagline","Tagline"]] },
    { key: "theme_colors", label: "🎨 Tema · Colores globales",
      color: true },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-[hsl(var(--audit-bg))] p-4 text-sm">
        <p className="font-semibold">Editor de contenido visual</p>
        <p className="mt-1 text-xs text-[hsl(var(--audit-muted))]">Edita textos, botones, imágenes y colores de la web pública sin tocar código. Los cambios se aplican al instante al guardar. Puedes restaurar defaults por sección.</p>
      </div>
      {SECTIONS.map((s) => {
        const val = content[s.key] || {};
        return (
          <details key={s.key} data-testid={`content-section-${s.key}`} className="rounded-md border border-[hsl(var(--audit-border))] bg-white" open={s.key === "home_hero"}>
            <summary className="flex cursor-pointer items-center justify-between px-4 py-3 font-semibold">
              <span>{s.label}</span>
              {dirty[s.key] && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">sin guardar</span>}
            </summary>
            <div className="border-t border-[hsl(var(--audit-border))] p-4">
              {s.color ? (
                <ThemeColorEditor val={val} onChange={(k, v) => setField(s.key, k, v)} />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {s.fields.map(([k, label, isLong]) => (
                    <label key={k} className={`block ${isLong ? "sm:col-span-2" : ""}`}>
                      <span className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--audit-muted))]">{label}</span>
                      {isLong ? (
                        <textarea rows={3} data-testid={`content-${s.key}-${k}`} value={val[k] || ""} onChange={(e) => setField(s.key, k, e.target.value)} className="mt-1 w-full rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
                      ) : (
                        <input data-testid={`content-${s.key}-${k}`} value={val[k] || ""} onChange={(e) => setField(s.key, k, e.target.value)} className="mt-1 w-full rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 text-sm" />
                      )}
                    </label>
                  ))}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <button onClick={() => save(s.key)} disabled={saving === s.key} data-testid={`content-save-${s.key}`} className="rounded-md bg-[hsl(var(--audit-text))] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {saving === s.key ? "Guardando…" : "Guardar cambios"}
                </button>
                <button onClick={() => reset(s.key)} data-testid={`content-reset-${s.key}`} className="rounded-md border border-[hsl(var(--audit-border))] px-4 py-2 text-sm">Restaurar defaults</button>
              </div>
            </div>
          </details>
        );
      })}
      <a href="/" target="_blank" rel="noreferrer" data-testid="content-preview-link" className="inline-block rounded-md border border-[hsl(var(--audit-border))] bg-white px-4 py-2 text-sm font-semibold">Vista previa (nueva pestaña) →</a>
    </div>
  );
}

function ThemeColorEditor({ val, onChange }) {
  const primaryHex = val.primary_hex || "#2a9dbb";
  const secondaryHex = val.secondary_hex || "#6B2C6E";
  const applyColor = (which, hex) => {
    const hsl = hexToHslString(hex);
    if (which === "primary") {
      onChange("primary_hex", hex);
      onChange("primary_hsl", hsl);
      onChange("primary_dark_hsl", darkenHslString(hsl, 13));
    } else {
      onChange("secondary_hex", hex);
      onChange("secondary_hsl", hsl);
      onChange("secondary_dark_hsl", darkenHslString(hsl, 10));
    }
  };
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="rounded-md border border-[hsl(var(--audit-border))] p-4">
        <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Primario (cyan)</div>
        <div className="flex items-center gap-3">
          <input type="color" value={primaryHex} onChange={(e) => applyColor("primary", e.target.value)} data-testid="theme-primary-color" className="h-12 w-12 cursor-pointer rounded border border-[hsl(var(--audit-border))]" />
          <input type="text" value={primaryHex} onChange={(e) => applyColor("primary", e.target.value)} className="flex-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 font-mono text-sm" />
        </div>
        <p className="mt-2 text-xs text-[hsl(var(--audit-muted))]">Usado en botones primarios y acentos cyan del logo.</p>
      </div>
      <div className="rounded-md border border-[hsl(var(--audit-border))] p-4">
        <div className="mb-2 font-mono text-xs uppercase tracking-widest text-[hsl(var(--audit-muted))]">Secundario (burdeos)</div>
        <div className="flex items-center gap-3">
          <input type="color" value={secondaryHex} onChange={(e) => applyColor("secondary", e.target.value)} data-testid="theme-secondary-color" className="h-12 w-12 cursor-pointer rounded border border-[hsl(var(--audit-border))]" />
          <input type="text" value={secondaryHex} onChange={(e) => applyColor("secondary", e.target.value)} className="flex-1 rounded-md border border-[hsl(var(--audit-border))] px-3 py-2 font-mono text-sm" />
        </div>
        <p className="mt-2 text-xs text-[hsl(var(--audit-muted))]">CTA principal, títulos resaltados, badge del logo.</p>
      </div>
      <div className="sm:col-span-2 rounded-md bg-[hsl(var(--audit-bg))] p-3 text-xs text-[hsl(var(--audit-muted))]">
        Al guardar, la web recargará automáticamente para aplicar los nuevos colores globalmente. Los valores HSL se calculan automáticamente desde el hex.
      </div>
    </div>
  );
}

